import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PlannedStartDto } from "../../autogen/bff-api";
import { plannedStartKey, upcomingMonths, usePlannedStartLabel } from "../../common/needs/plannedStart";

const MONTHS_OFFERED = 24;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  current?: PlannedStartDto | null;
  currentHasPassed?: boolean;
  onConfirm: (start: PlannedStartDto) => Promise<void>;
  failureMessage: (error: unknown) => string;
}

export const PlanNeedDialog = ({
  isOpen,
  onClose,
  current,
  currentHasPassed = false,
  onConfirm,
  failureMessage,
}: Props) => {
  const { t } = useTranslation();
  const label = usePlannedStartLabel();
  const offered = useMemo(() => upcomingMonths(new Date(), MONTHS_OFFERED), []);
  const [selected, setSelected] = useState<string>(current ? plannedStartKey(current) : "");
  const [isSaving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const chosen = offered.find((o) => plannedStartKey(o) === selected);

  const close = () => {
    if (isSaving) return;
    setFailure(null);
    onClose();
  };
  const confirm = async () => {
    if (!chosen) return;
    setSaving(true);
    setFailure(null);
    try {
      await onConfirm(chosen);
      onClose();
    } catch (e) {
      setFailure(failureMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{current ? t("Change planned start") : t("Plan the need")}</ModalHeader>
        <ModalCloseButton isDisabled={isSaving} />
        <ModalBody>
          <Stack spacing="4">
            {current && currentHasPassed && (
              <Alert status="warning" rounded="md" fontSize="sm">
                <AlertIcon />
                {t("Planned start was {{month}}, and that month has passed. Choose a new month.", {
                  month: label(current),
                })}
              </Alert>
            )}
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Planned start")}</FormLabel>
              <Select
                placeholder={t("Choose when the procurement starts") ?? undefined}
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                {offered.map((option) => (
                  <option key={plannedStartKey(option)} value={plannedStartKey(option)}>
                    {label(option)}
                  </option>
                ))}
              </Select>
              <FormHelperText>{t("The reporter sees when the need is planned.")}</FormHelperText>
            </FormControl>
            {failure && (
              <Alert status="error" rounded="md" fontSize="sm">
                <AlertIcon />
                {failure}
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={close} isDisabled={isSaving}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" isDisabled={!chosen} isLoading={isSaving} onClick={() => void confirm()}>
            {current ? t("Save") : t("Plan")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
